<!-- Hidden select with option for accessibility -->
<select [attr.aria-hidden]="true" tabindex="-1" style="position: absolute; opacity: 0; pointer-events: none; width: 0; height: 0; overflow: hidden; clip: rect(0, 0, 0, 0);">
  <option
    [value]="option.value"
    [selected]="isOptionSelected(option)"
    [disabled]="option.disabled">
    {{ getOptionLabel(option) }}
  </option>
</select>

<button
  type="button"
  class="relative w-full text-left cursor-pointer p-[10px] rounded-[16px] transition-colors duration-200 border-b border-option-default group"
  [ngClass]="{
    'bg-secondary-100 ': isOptionSelected(option),
    'hover:bg-secondary-100/60 ': !isOptionSelected(option),
    'bg-white border-l-transparent': !isOptionSelected(option),
    'opacity-50 cursor-not-allowed': option.disabled
  }"
  (mouseenter)="highlighted = true"
  (mouseleave)="highlighted = false"
  (click)="onOptionClick()"
  (keydown)="onOptionKeyDown($event)"
  [disabled]="option.disabled"
  [attr.aria-label]="getOptionLabel(option)"
  [attr.aria-selected]="isOptionSelected(option) ? 'true' : 'false'"
>
  <div class="flex items-center gap-2 w-full">
    <!-- Checkbox for multi-select -->
    <div *ngIf="multiple" 
         class="w-4 h-4 border-2 border-option-default rounded flex-shrink-0 flex items-center justify-center transition-colors duration-200"
         [class.border-primary-500]="isOptionSelected(option)"
         [class.bg-primary-500]="isOptionSelected(option)"
         [class.border-option-default]="!isOptionSelected(option)"
         [class.bg-option-checkbox-bg]="!isOptionSelected(option)">
      <div *ngIf="isOptionSelected(option)" class="w-full h-full bg-primary-500 border-primary-500 rounded flex items-center justify-center">
        <NiIcon *ngIf="checkIcon" [icon]="checkIcon" class="w-3 h-3 text-white"></NiIcon>
      </div>
    </div>
    
    <!-- Radio for single select -->
    <div *ngIf="!multiple && showRadioBox" 
         class="w-4 h-4 border-2 border-option-default rounded-full flex-shrink-0 flex items-center justify-center transition-colors duration-200"
         [class.border-primary-500]="isOptionSelected(option)"
         [class.border-option-default]="!isOptionSelected(option)">
      <div *ngIf="isOptionSelected(option)" 
           class="w-2 h-2 bg-primary-500 rounded-full"></div>
    </div>
    
    <!-- Leading Icon/Profile Image -->
    <div *ngIf="option['profileImage'] || option['icon']" class="flex-shrink-0 flex items-center">
      <!-- Profile Image with Fallback -->
      <div *ngIf="option['profileImage']" 
           class="w-8 h-8 rounded-full border-2 overflow-hidden flex items-center justify-center"
           [class.border-primary-500]="isOptionSelected(option)"
           [class.border-option-default]="!isOptionSelected(option)">
        <img [src]="option['profileImage']" 
             [alt]="getOptionLabel(option)"
             class="w-full h-full rounded-full object-cover"
             loading="lazy"
             decoding="async"
             (error)="onImageError($event, option, 'profileImage')"
             [style.display]="option['imageLoaded'] !== false ? 'block' : 'none'">
        <div *ngIf="!option['imageLoaded'] && !option['profileImage']" 
             class="w-full h-full flex items-center justify-center text-white font-medium text-sm "
             [class.bg-primary-500]="isOptionSelected(option)"
             [class.bg-option-fallback]="!isOptionSelected(option)">
          {{ getFirstLetter(option) }}
        </div>
      </div>
      <!-- Icon -->
      <NiIcon *ngIf="!option['profileImage'] && (option['icon'] !== undefined)" 
             [icon]="option['icon']" 
             class="w-5 h-5 flex-shrink-0"
             [class.text-primary-500]="isOptionSelected(option)"
             [class.text-option-icon]="!isOptionSelected(option)"></NiIcon>
    </div>
    
    <div class="flex-1 min-w-0 flex flex-col gap-[6px]">
      <div class=" font-weight-600  text-option-text text-[20px] whitespace-nowrap overflow-hidden text-ellipsis " 
           [class.text-primary-500]="isOptionSelected(option)"
           [class.font-semibold]="isOptionSelected(option)"
           [class.text-option-text]="!isOptionSelected(option)"
           [class.font-semibold]="!isOptionSelected(option)">
        {{ getOptionLabel(option) }}
      </div>
      <!-- Subtext/Subtitle -->
      <div *ngIf="option['subtext']" class="text-option-subtext  font-weight-400 text-[18px]">
        {{ option['subtext'] }}
      </div>
      <div *ngIf="option.code" class="text-xs leading-tight whitespace-nowrap overflow-hidden text-ellipsis"
           [class.text-option-subtext]="!isOptionSelected(option)"
           [class.font-normal]="!isOptionSelected(option)"
           [class.text-primary-500]="isOptionSelected(option)">{{ option.code }}</div>
    </div>
    
    <!-- Badge Count -->
    <div *ngIf="showBadge && option['badgeCount'] && option['badgeCount'] > 0" 
         class="bg-option-badge text-option-badge-text text-xs rounded-full flex items-center justify-center min-w-5 h-5 px-1.5 ml-2">
      {{ option['badgeCount'] }}
    </div>
    
    <!-- Trailing Badge -->
    <div *ngIf="option['badge']" class="bg-option-badge text-option-badge-text text-xs rounded-full flex items-center justify-center min-w-5 h-5 px-1.5 ml-2">
      {{ option['badge'] }}
    </div>
    <!-- Trailing Shortcut -->
    <div *ngIf="option['shortcut']" class="text-option-shortcut text-xs ml-2">
      {{ option['shortcut'] }}
    </div>
    
    <!-- Favorite Star Toggle -->
    <button *ngIf="showFavoriteToggle" 
            type="button"
            class="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded transition-colors duration-200 hover:bg-option-favorite-hover cursor-pointer border-0 bg-transparent p-0"
            (click)="onFavoriteToggle($event)"
            [attr.aria-label]="option['isFavorite'] ? 'Remove from favorites' : 'Add to favorites'">
      <NiIcon *ngIf="option['isFavorite'] !== undefined" [icon]="option['isFavorite'] ? 'star_filled' : 'star_outline'" 
             class="w-4 h-4 transition-colors duration-200"
             [class.text-option-favorite-active]="option['isFavorite']"
             [class.text-option-favorite-inactive]="!option['isFavorite']"></NiIcon>
    </button>
    
    <!-- Trailing Icon/Profile Image -->
    <div *ngIf="option['trailingProfileImage'] || option['trailingIcon']" class="flex-shrink-0 flex items-center">
      <!-- Trailing Profile Image with Fallback -->
      <div *ngIf="option['trailingProfileImage']" 
           class="w-6 h-6 rounded-full border-2 overflow-hidden flex items-center justify-center"
           [class.border-primary-500]="isOptionSelected(option)"
           [class.border-option-default]="!isOptionSelected(option)">
        <img [src]="option['trailingProfileImage']" 
             [alt]="getOptionLabel(option) + ' trailing'"
             class="w-full h-full rounded-full object-cover"
             loading="lazy"
             decoding="async"
             (error)="onImageError($event, option, 'trailingProfileImage')"
             [style.display]="option['trailingImageLoaded'] !== false ? 'block' : 'none'">
        <div *ngIf="!option['trailingImageLoaded']" 
             class="w-full h-full flex items-center justify-center text-white font-medium text-xs"
             [class.bg-primary-500]="isOptionSelected(option)"
             [class.bg-option-fallback]="!isOptionSelected(option)">
          {{ getFirstLetter(option) }}
        </div>
      </div>
      <!-- Trailing Icon -->
      <NiIcon *ngIf="!option['trailingProfileImage'] && (option['trailingIcon'] || 'arrow_right_rounded')" 
             [icon]="option['trailingIcon'] || 'arrow_right_rounded'" 
             class="w-4 h-4 flex-shrink-0"
             [class.text-primary-500]="isOptionSelected(option)"
             [class.text-option-icon]="!isOptionSelected(option)"></NiIcon>
    </div>
  </div>
</button> 